"use client";

import React, { useEffect, useState } from "react";
import { isAuthErrorLike, signOutAndGoToSignIn } from "@/lib/clientAuthError";
import {
  canAutoReload,
  isReloadRecoverableError,
  performAutoReload,
} from "@/lib/recoverableError";
import { classifyRuntimeError } from "@/lib/runtimeError";

// Global error boundary: used when the error happens in the root layout or
// before a route's error.tsx can handle it. Must return <html><body>.
// NOTE: global CSS may not have loaded when this renders, so the card is
// styled inline — no Tailwind, no app components.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const { isTimeout, isNetwork, isServerError } = classifyRuntimeError(error);

  // Hydration/stale-chunk errors are fixed by one clean reload — do it
  // automatically (once per page per minute) instead of showing the card.
  const [autoReloading, setAutoReloading] = useState(
    () => isReloadRecoverableError(error) && canAutoReload(),
  );

  const title = isTimeout
    ? "The request took too long"
    : isServerError
      ? "We couldn’t reach the server"
      : isNetwork
        ? "Connection problem"
        : "Something went wrong";
  const description = isTimeout
    ? "Our servers are taking longer than expected. Please try again in a moment."
    : isServerError
      ? "The server didn’t respond. Please refresh the page — it’s usually temporary."
      : isNetwork
        ? "Please check your internet connection and try again."
        : "An unexpected error occurred. Refresh the page — if it keeps happening, the team has already been notified.";

  useEffect(() => {
    if (isAuthErrorLike(error)) {
      void signOutAndGoToSignIn();
      return;
    }

    try {
      fetch("/api/report-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: "app.global-error.boundary",
          name: error?.name,
          message: error?.message,
          stack: error?.stack,
          digest: (error as any)?.digest,
          isTimeout,
          isNetwork,
          isServerError,
          autoReloaded: autoReloading,
          path:
            typeof window !== "undefined"
              ? window.location.pathname
              : undefined,
        }),
        keepalive: true,
      });
    } catch {}
    if (autoReloading && !performAutoReload()) {
      // Guard stamp failed to write — reload never started. Show the error
      // card instead of leaving the user on a spinner that never resolves.
      setAutoReloading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (autoReloading) {
    return (
      <html>
        <body
          style={{
            margin: 0,
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#0a0a0f",
            fontFamily:
              "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          <p style={{ color: "#a3a3b2", fontSize: 14 }}>Refreshing the page…</p>
        </body>
      </html>
    );
  }

  return (
    <html>
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0a0a0f",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: "100%",
            margin: 24,
            padding: 32,
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "#15151c",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 40, lineHeight: 1 }}>⚠️</div>
          <h2
            style={{
              margin: "16px 0 8px",
              fontSize: 20,
              fontWeight: 600,
              color: "#f4f4f6",
            }}
          >
            {title}
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.6,
              color: "#a3a3b2",
            }}
          >
            {description}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 24,
              padding: "10px 24px",
              borderRadius: 9999,
              border: "none",
              background: "#4f46e5",
              color: "#ffffff",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            ↻ Refresh page
          </button>
        </div>
      </body>
    </html>
  );
}
